import { useContext, useState } from "react"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { LocalAiLeadMapping } from "@/api/admin-api"

import { deleteLocalAiLead, refreshLocalAiLeadAssignments } from "@/api/admin-api"
import { Pencil, RefreshCw, Trash2, UserCog } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Spinner } from "@/components/ui/spinner"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { AdminFocusedSurfaceContext } from "@/features/admin-dashboard/admin-focused-surface-context"
import { AssignedUserVisitsCard } from "@/features/local-ai-lead/components/assigned-user-visits-card"

import { AddLocalAiLeadDialog } from "./add-local-ai-lead-dialog"
import { useLocalAiLeadConfigQuery } from "./local-ai-lead-config-query"
import { LocalAiLeadGroupOverview } from "./local-ai-lead-group-overview"
import { LocalAiLeadSettingsSection } from "./local-ai-lead-settings-section"
import { LOCAL_AI_LEADS_QUERY_KEY, useLocalAiLeadsQuery } from "./local-ai-leads-query"

// ─── Query hooks ────────────────────────────────────────────────────────────

function useDeleteLocalAiLeadMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (oid: string) => deleteLocalAiLead(oid),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LOCAL_AI_LEADS_QUERY_KEY })
    },
  })
}

function useRefreshLocalAiLeadAssignmentsMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: refreshLocalAiLeadAssignments,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LOCAL_AI_LEADS_QUERY_KEY })
    },
  })
}

// ─── DeleteConfirmDialog ────────────────────────────────────────────────────

type DeleteConfirmDialogProps = {
  open: boolean
  leadName: string
  onConfirm: () => void
  onCancel: () => void
  isPending: boolean
}

function DeleteConfirmDialog({
  open,
  leadName,
  onConfirm,
  onCancel,
  isPending,
}: DeleteConfirmDialogProps) {
  const { t } = useTranslation()

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onCancel()
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("adminDashboard.localAiLeads.deleteTitle", "Delete Local AI Lead")}
          </DialogTitle>
        </DialogHeader>
        <p className="text-muted-foreground text-sm">
          {t("adminDashboard.localAiLeads.deleteConfirm", {
            name: leadName,
            defaultValue: `Are you sure you want to remove ${leadName} as a Local AI Lead? This cannot be undone.`,
          })}
        </p>
        <DialogFooter>
          <Button disabled={isPending} variant="outline" onClick={onCancel}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button disabled={isPending} variant="destructive" onClick={onConfirm}>
            {isPending ? <Spinner className="mr-2 size-4" /> : null}
            {t("common.delete", "Delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── LocalAiLeadsManagementCard ─────────────────────────────────────────────

export function LocalAiLeadsManagementCard({
  canEditCosts,
  showSettings = true,
}: {
  canEditCosts: boolean
  showSettings?: boolean
}) {
  const { t } = useTranslation()
  const isFocusedSurface = useContext(AdminFocusedSurfaceContext)
  const { data, isLoading } = useLocalAiLeadsQuery()
  const deleteMutation = useDeleteLocalAiLeadMutation()
  // Config is read here only to feed the add/edit dialogs; the settings form
  // owns its own copy of the query (React Query dedupes by key).
  const configQuery = useLocalAiLeadConfigQuery()
  const refreshMutation = useRefreshLocalAiLeadAssignmentsMutation()

  const [isAddOpen, setIsAddOpen] = useState(false)
  const [editingLead, setEditingLead] = useState<LocalAiLeadMapping | null>(null)
  const [deletingLead, setDeletingLead] = useState<LocalAiLeadMapping | null>(null)

  const localAiLeads = data?.localAiLeads ?? []

  // Configured grouping attribute / scope group, passed to the add/edit dialogs.
  const currentGroupAttr = configQuery.data?.local_ai_lead_config.group_attribute
  const currentGroupId = configQuery.data?.local_ai_lead_config.user_group_id ?? null

  const handleDeleteConfirm = async () => {
    if (!deletingLead) return
    try {
      await deleteMutation.mutateAsync(deletingLead.ambassadorOid)
      toast.success(t("adminDashboard.localAiLeads.deletedToast", "Local AI Lead removed"))
      setDeletingLead(null)
    } catch (error) {
      toast.error(
        t("adminDashboard.localAiLeads.deleteErrorToast", "Failed to remove Local AI Lead"),
        { description: error instanceof Error ? error.message : String(error) }
      )
    }
  }

  const handleRefresh = async () => {
    try {
      const result = await refreshMutation.mutateAsync()
      const { totalAddedUsers, leadsRefreshed, leadsSkipped } = result
      if (totalAddedUsers === 0 && leadsSkipped === 0) {
        toast.success(
          t(
            "adminDashboard.localAiLeads.refreshNoChangesToast",
            "All Local AI Leads are up to date"
          )
        )
      } else {
        toast.success(
          t(
            "adminDashboard.localAiLeads.refreshedToast",
            "Added {{userCount}} users across {{leadCount}} Local AI Leads",
            { userCount: totalAddedUsers, leadCount: leadsRefreshed }
          ),
          leadsSkipped > 0
            ? {
                description: t(
                  "adminDashboard.localAiLeads.refreshSkippedDescription",
                  "{{count}} skipped (missing attribute or lookup failed)",
                  { count: leadsSkipped }
                ),
              }
            : undefined
        )
      }
    } catch (error) {
      toast.error(
        t(
          "adminDashboard.localAiLeads.refreshErrorToast",
          "Failed to refresh Local AI Lead assignments"
        ),
        { description: error instanceof Error ? error.message : String(error) }
      )
    }
  }

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Spinner />
        </div>
      )
    }
    if (localAiLeads.length === 0) {
      return (
        <p className="text-muted-foreground py-6 text-center text-sm">
          {t("adminDashboard.localAiLeads.empty", "No Local AI Leads configured yet.")}
        </p>
      )
    }
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("adminDashboard.localAiLeads.colLead", "Local AI Lead")}</TableHead>
            <TableHead>
              {t("adminDashboard.localAiLeads.colUsersAssigned", "Users Assigned")}
            </TableHead>
            <TableHead className="text-right">
              {t("adminDashboard.localAiLeads.colActions", "Actions")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {localAiLeads.map((lead) => (
            <TableRow key={lead.id}>
              <TableCell>
                <div className="font-medium">{lead.ambassadorName}</div>
                <div className="text-muted-foreground text-xs">{lead.ambassadorEmail}</div>
              </TableCell>
              <TableCell>
                <Badge variant="secondary">{lead.assignedUsers.length}</Badge>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  <Button
                    aria-label={`${t("adminDashboard.localAiLeads.editButton", "Edit")} ${lead.ambassadorName}`}
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingLead(lead)}
                  >
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    aria-label={`${t("adminDashboard.localAiLeads.deleteButton", "Delete")} ${lead.ambassadorName}`}
                    className="text-destructive hover:text-destructive"
                    size="sm"
                    variant="ghost"
                    onClick={() => setDeletingLead(lead)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )
  }

  return (
    <>
      <Card className="rounded-lg shadow-sm">
        <CardHeader>
          <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
            {!isFocusedSurface ? (
              <CardTitle className="flex items-center gap-2 text-lg">
                <UserCog className="size-5" />
                {t("adminDashboard.localAiLeads.title", "Local AI Leads")}
              </CardTitle>
            ) : null}
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
              <Button
                disabled={refreshMutation.isPending || isLoading}
                size="sm"
                variant="outline"
                title={t(
                  "adminDashboard.localAiLeads.refreshTooltip",
                  "Sync assignments from Entra by grouping attribute. Existing users are preserved."
                )}
                onClick={() => void handleRefresh()}
              >
                {refreshMutation.isPending ? (
                  <Spinner className="mr-2 size-4" />
                ) : (
                  <RefreshCw className="mr-2 size-4" />
                )}
                {t("adminDashboard.localAiLeads.refreshButton", "Refresh")}
              </Button>
              <Button size="sm" onClick={() => setIsAddOpen(true)}>
                {t("adminDashboard.localAiLeads.addButton", "Add Local AI Lead")}
              </Button>
            </div>
          </div>
          {!isFocusedSurface ? (
            <p className="text-muted-foreground text-sm">
              {t(
                "adminDashboard.localAiLeads.description",
                "Manage which users have Local AI Lead access and which users they can view"
              )}
            </p>
          ) : null}
        </CardHeader>
        <CardContent>
          {configQuery.data?.local_ai_lead_config.dashboard_sections?.group_overview ? (
            <LocalAiLeadGroupOverview
              canEditOverride={canEditCosts}
              currency={configQuery.data.local_ai_lead_config.fixed_cost_currency ?? "USD"}
            />
          ) : null}
          {configQuery.data?.local_ai_lead_config.assigned_visits_enabled || canEditCosts ? (
            <AssignedUserVisitsCard
              canEnable={canEditCosts}
              enabled={configQuery.data?.local_ai_lead_config.assigned_visits_enabled === true}
              mode="admin"
            />
          ) : null}
          {/* ── Settings: grouping attribute + fixed costs ── */}
          {showSettings ? <LocalAiLeadSettingsSection canEditCosts={canEditCosts} /> : null}

          {/* ── Local AI Leads table ── */}
          {renderBody()}
        </CardContent>
      </Card>

      {/* Add dialog — conditionally mounted so state resets on each open */}
      {isAddOpen && (
        <AddLocalAiLeadDialog
          open
          groupAttribute={currentGroupAttr}
          scopeGroup={currentGroupId ?? undefined}
          onOpenChange={(open) => {
            if (!open) setIsAddOpen(false)
          }}
        />
      )}

      {/* Edit dialog */}
      {editingLead ? (
        <AddLocalAiLeadDialog
          editing={editingLead}
          groupAttribute={currentGroupAttr}
          open={!!editingLead}
          scopeGroup={currentGroupId ?? undefined}
          onOpenChange={(open) => {
            if (!open) setEditingLead(null)
          }}
        />
      ) : null}

      {/* Delete confirm dialog */}
      {deletingLead ? (
        <DeleteConfirmDialog
          isPending={deleteMutation.isPending}
          leadName={deletingLead.ambassadorName}
          open={!!deletingLead}
          onCancel={() => setDeletingLead(null)}
          onConfirm={() => void handleDeleteConfirm()}
        />
      ) : null}
    </>
  )
}
